import { useState, useEffect, useCallback, useRef } from 'react'
import { FixedSizeList } from 'react-window'
import { useAuth } from '../../../context/AuthContext'
import BulkTagModal from '../../../components/BulkTagModal'
import MuxUpload from './MuxUpload'
import MigrateFromCFModal from './MigrateFromCFModal'
import BulkCreateCatalogModal from './BulkCreateCatalogModal'
import { readCache, writeCache, invalidate as invalidateMuxCache } from '../../../lib/muxAssetsCache'
import '../../../components/BulkTagModal.css'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import './MuxStreamManager.css'

const MUX_ROW_HEIGHT = 60

// ============================================================
// HELPERS
// ============================================================

function formatDuration(secs) {
  if (!secs) return '—'
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = Math.floor(secs % 60)
  if (h > 0) return `${h}h ${m}m ${s}s`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

function formatBytes(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
  if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB'
  return (bytes / 1073741824).toFixed(2) + ' GB'
}

function formatDate(val) {
  if (!val) return '—'
  // Mux created_at can be: Unix seconds (number), stringified Unix seconds, or ISO string
  let num = typeof val === 'string' ? Number(val) : val
  let ts
  if (!isNaN(num) && num > 0) {
    // Unix timestamp — if less than 1e12 it's seconds, convert to ms
    ts = num < 1e12 ? num * 1000 : num
  } else {
    ts = val // ISO string or other date format
  }
  const d = new Date(ts)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function getMuxThumbnail(playbackId) {
  if (!playbackId) return null
  return `https://image.mux.com/${playbackId}/thumbnail.jpg?width=480&height=270&time=5`
}

function getMuxHls(playbackId) {
  if (!playbackId) return null
  return `https://stream.mux.com/${playbackId}.m3u8`
}

function getPlaybackId(asset) {
  if (!asset?.playback_ids?.length) return null
  return asset.playback_ids[0].id
}

function getAssetMeta(asset) {
  return {
    title: asset.meta?.title || null,
    creator_id: asset.meta?.creator_id || null,
    external_id: asset.meta?.external_id || null,
    source_file: asset.passthrough || null,
  }
}

function getAssetTitle(asset) {
  return asset.meta?.title || asset.passthrough || asset.id
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function MuxStreamManager() {
  const { api } = useAuth()

  // Asset list state
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [selected, setSelected] = useState(null)

  // Search + Tag filter
  const [searchTerm, setSearchTerm] = useState('')
  const [allTags, setAllTags] = useState([])
  const [activeTag, setActiveTag] = useState('')
  const [tagFilterIds, setTagFilterIds] = useState(null) // null = no filter

  // Sort
  const [sortKey, setSortKey] = useState(null)
  const [sortDir, setSortDir] = useState('asc')

  // Per-asset tags map: { tbtv_id: [tag1, tag2, ...] }
  const [assetTagsMap, setAssetTagsMap] = useState({})

  // Content library link tracking: { assetId: { linked, entries: [{ tbtv_id, title }] } }
  const [muxLinks, setMuxLinks] = useState({})

  // Resolved tbtv_id map for assets without external_id: { assetId: tbtvId }
  const [resolvedIds, setResolvedIds] = useState({})
  // Mirror to a ref so the enrichment effect can read latest without depending on it
  const resolvedIdsRef = useRef({})
  useEffect(() => { resolvedIdsRef.current = resolvedIds }, [resolvedIds])

  // Bulk selection
  const [checked, setChecked] = useState(new Set())
  const [showBulkTag, setShowBulkTag] = useState(false)
  const [showBulkCreateCatalog, setShowBulkCreateCatalog] = useState(false)

  // Archive restore
  const [archiveFiles, setArchiveFiles] = useState([])
  const [archiveLoading, setArchiveLoading] = useState(false)
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  const [showMigrateModal, setShowMigrateModal] = useState(false)
  const [restoreBusy, setRestoreBusy] = useState(false)
  const [restoreStatus, setRestoreStatus] = useState('')

  // Player — video.js owns its DOM subtree to avoid React reconciliation conflicts
  const playerRef = useRef(null)
  const playerContainerRef = useRef(null)
  const [playingAssetId, setPlayingAssetId] = useState(null)
  const hlsUrlRef = useRef(null)

  // Virtualized table height: leave room for header, filters, detail panel, upload zone.
  // Min 400 so tiny windows still show several rows.
  const [tableHeight, setTableHeight] = useState(() =>
    Math.max(400, (typeof window !== 'undefined' ? window.innerHeight : 900) - 380)
  )
  useEffect(() => {
    const calc = () => setTableHeight(Math.max(400, window.innerHeight - 380))
    window.addEventListener('resize', calc)
    return () => window.removeEventListener('resize', calc)
  }, [])

  // Cleanup player on unmount
  useEffect(() => {
    return () => {
      if (playerRef.current) { playerRef.current.dispose(); playerRef.current = null }
    }
  }, [])

  // Callback ref on the wrapper div — creates <video> imperatively so React never owns it
  const playerWrapperRef = useCallback((node) => {
    if (!node) return
    playerContainerRef.current = node
    const videoEl = document.createElement('video')
    videoEl.className = 'video-js vjs-default-skin'
    node.appendChild(videoEl)
    const player = videojs(videoEl, {
      controls: true,
      autoplay: true,
      fluid: true,
      sources: [{ src: hlsUrlRef.current, type: 'application/x-mpegURL' }]
    })
    playerRef.current = player
  }, [])

  const stopPlayer = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.dispose()
      playerRef.current = null
    }
    // Clear any leftover DOM from the container
    if (playerContainerRef.current) {
      playerContainerRef.current.innerHTML = ''
      playerContainerRef.current = null
    }
    setPlayingAssetId(null)
  }, [])

  const playAsset = useCallback((asset) => {
    if (playerRef.current) { playerRef.current.dispose(); playerRef.current = null }
    const pid = getPlaybackId(asset)
    if (!pid) return
    hlsUrlRef.current = getMuxHls(pid)
    setPlayingAssetId(asset.id)
  }, [])

  // Load Mux assets — stale-while-revalidate via sessionStorage cache.
  // Pass forceFresh=true to skip cache (used by the manual refresh button).
  const loadAssets = useCallback(async (forceFresh = false) => {
    if (!forceFresh) {
      const cached = readCache()
      if (cached) {
        setAssets(cached)
        setLoading(false)
        setStatus(`${cached.length} assets (cached)`)
      } else {
        setLoading(true)
      }
    } else {
      setLoading(true)
    }
    setStatus(prev => forceFresh ? '' : prev)
    try {
      const path = forceFresh ? '/mux/assets?nocache=1' : '/mux/assets'
      const data = await api(path)
      const fresh = data.data || data || []
      // Avoid an extra render+enrichment pass when cached === fresh
      setAssets(prev => {
        if (
          prev.length === fresh.length &&
          prev.every((a, i) => a.id === fresh[i].id && a.updated_at === fresh[i].updated_at)
        ) {
          return prev
        }
        return fresh
      })
      writeCache(fresh)
      setStatus(`${fresh.length} assets loaded`)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
      if (forceFresh) setAssets([])
    }
    setLoading(false)
  }, [api])

  // Load archive files for restore picker
  const loadArchiveFiles = useCallback(async () => {
    setArchiveLoading(true)
    try {
      const data = await api('/archive/list-picker')
      setArchiveFiles(data.files || data || [])
    } catch (e) {
      setArchiveFiles([])
    }
    setArchiveLoading(false)
  }, [api])

  useEffect(() => { loadAssets() }, [loadAssets])

  // Load all tags for filter dropdown and autocomplete
  const refreshTags = useCallback(() => {
    api('/tags').then(d => setAllTags(d.tags || [])).catch(() => {})
  }, [api])

  useEffect(() => { refreshTags() }, [refreshTags])

  // Single enrichment pass per assets change. Inlined to avoid the
  // useCallback/useEffect dependency cycle that previously caused
  // setMuxLinks → callback identity changed → effect re-ran → loop.
  // Resolution order for each asset's tbtv_id:
  //   external_id > muxLinks (content lib) > resolvedIds > archive_key lookup
  useEffect(() => {
    if (!assets.length) return
    let cancelled = false

    ;(async () => {
      // 1. Build needs-resolve list using a *snapshot* of resolvedIds.
      //    Reading from resolvedIds via ref avoids retriggering the effect.
      const needsResolve = assets.filter(a => {
        if (a.meta?.external_id) return false
        if (resolvedIdsRef.current[a.id]) return false
        return !!a.passthrough
      })
      const newResolved = {}
      if (needsResolve.length) {
        try {
          const data = await api('/content/lookup-keys', {
            method: 'POST',
            body: JSON.stringify({ archive_keys: needsResolve.map(a => a.passthrough) }),
          })
          if (cancelled) return
          for (const asset of needsResolve) {
            const found = data.results?.[asset.passthrough]
            if (found) newResolved[asset.id] = found
          }
        } catch {}
      }

      // 2. Content library link lookup (batch by playback ID)
      const assetsWithPids = assets
        .map(a => ({ assetId: a.id, playbackId: getPlaybackId(a) }))
        .filter(a => a.playbackId)
      let linksData = {}
      if (assetsWithPids.length) {
        try {
          const data = await api('/mux/content-links/batch', {
            method: 'POST',
            body: JSON.stringify({ assets: assetsWithPids }),
          })
          if (cancelled) return
          linksData = data.results || {}
        } catch (e) {
          console.error('Failed to load mux content links:', e)
        }
      }

      // 3. Collect tbtv_ids using fresh data from steps 1+2 plus prior resolvedIds
      const tbtvIds = new Set()
      for (const asset of assets) {
        const id = asset.meta?.external_id
          || linksData[asset.id]?.entries?.[0]?.tbtv_id
          || resolvedIdsRef.current[asset.id]
          || newResolved[asset.id]
        if (id) tbtvIds.add(id)
      }

      // 4. Batch tag lookup
      let tagsMap = {}
      if (tbtvIds.size) {
        try {
          const data = await api('/tags/batch', {
            method: 'POST',
            body: JSON.stringify({ tbtv_ids: [...tbtvIds] }),
          })
          if (cancelled) return
          tagsMap = data.results || {}
        } catch {}
      }

      if (cancelled) return
      if (Object.keys(newResolved).length) {
        setResolvedIds(prev => ({ ...prev, ...newResolved }))
      }
      setMuxLinks(linksData)
      setAssetTagsMap(tagsMap)
    })()

    return () => { cancelled = true }
  }, [assets, api])

  // Tag filter: fetch matching content IDs when tag changes
  useEffect(() => {
    if (!activeTag) {
      setTagFilterIds(null)
      return
    }
    api(`/tags/filter?tag=${encodeURIComponent(activeTag)}`)
      .then(d => {
        // Collect tbtv_ids and archive_keys to match against Mux assets
        const tbtvIds = new Set((d.items || d.results || []).map(r => r.tbtv_id).filter(Boolean))
        const archiveKeys = new Set((d.archive_keys || []).filter(Boolean))
        setTagFilterIds({ tbtvIds, archiveKeys })
      })
      .catch(() => setTagFilterIds({ tbtvIds: new Set(), archiveKeys: new Set() }))
  }, [activeTag, api])

  function handleTagFilter(tag) {
    setActiveTag(tag)
    setChecked(new Set())
  }

  // Match a Mux asset against tag filter
  function assetMatchesTagFilter(asset) {
    if (!tagFilterIds) return true
    const tbtvId = asset.meta?.external_id || muxLinks[asset.id]?.entries?.[0]?.tbtv_id || resolvedIds[asset.id]
    const archiveKey = asset.passthrough
    if (tbtvId && tagFilterIds.tbtvIds.has(tbtvId)) return true
    if (archiveKey && tagFilterIds.archiveKeys.has(archiveKey)) return true
    return false
  }

  // Filtered asset list (tag filter + search)
  const filteredByTag = tagFilterIds !== null
    ? assets.filter(assetMatchesTagFilter)
    : assets
  function handleSort(key) {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc')
      else { setSortKey(null); setSortDir('asc') }
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  let displayAssets = searchTerm
    ? filteredByTag.filter(a => getAssetTitle(a).toLowerCase().includes(searchTerm.toLowerCase()))
    : filteredByTag

  if (sortKey) {
    displayAssets = [...displayAssets].sort((a, b) => {
      let av, bv
      switch (sortKey) {
        case 'title':
          av = getAssetTitle(a).toLowerCase()
          bv = getAssetTitle(b).toLowerCase()
          break
        case 'status':
          av = a.status || ''
          bv = b.status || ''
          break
        case 'duration':
          av = a.duration || 0
          bv = b.duration || 0
          break
        case 'resolution':
          av = a.resolution_tier || a.max_resolution_tier || ''
          bv = b.resolution_tier || b.max_resolution_tier || ''
          break
        case 'created':
          av = a.created_at || ''
          bv = b.created_at || ''
          break
        default:
          return 0
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1
      if (av > bv) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }

  // Detect duplicate titles for highlighting
  const titleCounts = {}
  displayAssets.forEach(a => {
    const t = getAssetTitle(a).toLowerCase()
    titleCounts[t] = (titleCounts[t] || 0) + 1
  })
  const duplicateTitles = new Set(Object.keys(titleCounts).filter(t => titleCounts[t] > 1))

  // Bulk selection handlers
  function handleToggle(assetId) {
    setChecked(prev => {
      const next = new Set(prev)
      if (next.has(assetId)) next.delete(assetId)
      else next.add(assetId)
      return next
    })
  }

  function handleToggleAll() {
    if (checked.size === displayAssets.length) {
      setChecked(new Set())
    } else {
      setChecked(new Set(displayAssets.map(a => a.id)))
    }
  }

  // Scan content library for all Mux HLS links and refresh linkage data
  async function handleScanLinks() {
    setStatus('Scanning content library for Mux links...')
    const assetsWithPids = assets
      .map(a => ({ assetId: a.id, playbackId: getPlaybackId(a) }))
      .filter(a => a.playbackId)
    if (!assetsWithPids.length) {
      setStatus('No Mux assets with playback IDs found to scan')
      return
    }
    try {
      const data = await api('/mux/content-links/batch', {
        method: 'POST',
        body: JSON.stringify({ assets: assetsWithPids })
      })
      if (data.results) setMuxLinks(data.results)
      const linked = Object.values(data.results || {}).filter(l => l.linked).length
      const debug = data._debug || {}
      setStatus(`✅ Scan complete — ${linked} linked to content library (${debug.muxRowsFound || 0} MUX entries found in DB, ${debug.uniquePlaybackIds || 0} unique playback IDs, ${assetsWithPids.length} Mux assets checked)`)
      console.log('Mux link scan debug:', debug)
    } catch (e) {
      setStatus(`Scan failed: ${e.message}`)
    }
  }

  function clearSelection() {
    setChecked(new Set())
  }

  function getCheckedItems() {
    // For BulkTagModal: prefer content library tbtv_id, fall back to archive_key
    return assets
      .filter(a => checked.has(a.id))
      .map(a => {
        const linkedTbtvId = a.meta?.external_id || muxLinks[a.id]?.entries?.[0]?.tbtv_id || resolvedIds[a.id]
        return {
          id: linkedTbtvId || a.passthrough || a.id,
          title: getAssetTitle(a),
          _hasTbtvId: !!linkedTbtvId,
        }
      })
  }

  // Delete a Mux asset — warn if linked to content library
  const handleDelete = async (assetId) => {
    const link = muxLinks[assetId]
    if (link?.linked) {
      const titles = link.entries.map(e => `  • ${e.title}`).join('\n')
      if (!confirm(
        `⚠️ This Mux asset is used by ${link.entries.length} content library item${link.entries.length > 1 ? 's' : ''}:\n\n${titles}\n\nDeleting it will break playback for these items. Delete anyway?`
      )) return
    } else {
      if (!confirm('Delete this asset from Mux? This cannot be undone.')) return
    }
    try {
      await api(`/mux/assets/${assetId}`, { method: 'DELETE' })
      setStatus('Asset deleted')
      if (selected?.id === assetId) { stopPlayer(); setSelected(null) }
      invalidateMuxCache()
      loadAssets(true)
    } catch (e) {
      setStatus(`Delete failed: ${e.message}`)
    }
  }

  // Bulk delete Mux assets — warn about linked items
  const handleBulkDelete = async () => {
    if (checked.size === 0) return
    const ids = Array.from(checked)

    // Check for linked assets
    const linkedIds = ids.filter(id => muxLinks[id]?.linked)
    if (linkedIds.length > 0) {
      const linkedTitles = linkedIds.slice(0, 5).map(id => {
        const asset = assets.find(a => a.id === id)
        const count = muxLinks[id].entries.length
        return `  • ${getAssetTitle(asset)} (${count} content library item${count > 1 ? 's' : ''})`
      }).join('\n')
      const more = linkedIds.length > 5 ? `\n  ...and ${linkedIds.length - 5} more` : ''
      if (!confirm(
        `⚠️ ${linkedIds.length} of ${ids.length} selected asset(s) are linked to the content library:\n\n${linkedTitles}${more}\n\nDeleting them will break playback. Delete all ${ids.length} anyway?`
      )) return
    } else {
      if (!confirm(`Delete ${ids.length} asset(s) from Mux? This cannot be undone!`)) return
    }

    let deleted = 0
    let errors = 0
    for (const assetId of ids) {
      try {
        await api(`/mux/assets/${assetId}`, { method: 'DELETE' })
        deleted++
        setStatus(`Deleting ${deleted}/${ids.length}...`)
      } catch {
        errors++
      }
    }
    if (selected && checked.has(selected.id)) { stopPlayer(); setSelected(null) }
    clearSelection()
    setStatus(`✅ Deleted ${deleted} asset(s)${errors ? ` (${errors} errors)` : ''}`)
    invalidateMuxCache()
    loadAssets(true)
  }

  // Restore archive files to Mux (supports batch)
  const [restoreResults, setRestoreResults] = useState([])
  const handleRestoreToMux = async (items) => {
    // items: [{ filename, title }, ...]
    setRestoreBusy(true)
    setRestoreResults([])
    const results = []
    for (let i = 0; i < items.length; i++) {
      setRestoreStatus(`Ingesting ${i + 1} of ${items.length}…`)
      try {
        const res = await api('/mux/ingest', {
          method: 'POST',
          body: JSON.stringify({ filename: items[i].filename, title: items[i].title }),
        })
        // Check for inherited tags from archive content record
        let copiedTags = []
        if (res.tbtv_id) {
          try {
            const tagData = await api(`/tags/${res.tbtv_id}`)
            copiedTags = tagData.tags || []
          } catch {}
        }
        results.push({ filename: items[i].filename, ok: true, asset_id: res.asset_id, copiedTags })
      } catch (e) {
        results.push({ filename: items[i].filename, ok: false, error: e.message })
      }
      setRestoreResults([...results])
    }
    const ok = results.filter(r => r.ok).length
    const fail = results.filter(r => !r.ok).length
    setRestoreStatus(`${ok} ingested${fail ? `, ${fail} failed` : ''}`)
    setRestoreBusy(false)
    invalidateMuxCache()
    loadAssets(true)
    refreshTags()
  }

  return (
    <div className="mux-manager">
      {/* Header */}
      <div className="mux-header">
        <div>
          <h1>Mux Stream Manager</h1>
          <p className="mux-subtitle">Manage video assets hosted on Mux.com</p>
        </div>
        <div className="mux-header-right">
          {status && <span className="mux-status">{status}</span>}
          <button className="btn btn-ghost" onClick={handleScanLinks} disabled={loading}>
            🔗 Scan Links
          </button>
          <button className="btn btn-ghost" onClick={() => setShowMigrateModal(true)}>
            Migrate from CF
          </button>
          <button className="btn btn-ghost" onClick={() => { setShowRestoreModal(true); loadArchiveFiles() }}>
            Restore from Archive
          </button>
          <button className="btn btn-ghost" onClick={() => { invalidateMuxCache(); loadAssets(true) }} disabled={loading}>
            {loading ? '…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <AssetDetail
          asset={selected}
          onClose={() => { stopPlayer(); setSelected(null) }}
          onDelete={handleDelete}
          onPlay={playAsset}
          playingAssetId={playingAssetId}
          playerWrapperRef={playerWrapperRef}
          stopPlayer={stopPlayer}
          api={api}
          allTags={allTags}
          muxLinks={muxLinks}
          onAssetUpdated={() => { invalidateMuxCache(); loadAssets(true) }}
          onTagsSaved={async (assetId, tbtvId) => {
            if (assetId && tbtvId) {
              setResolvedIds(prev => ({ ...prev, [assetId]: tbtvId }))
              // Directly fetch tags for this specific ID and merge into map
              try {
                const tagData = await api(`/tags/${tbtvId}`)
                setAssetTagsMap(prev => ({ ...prev, [tbtvId]: tagData.tags || [] }))
              } catch {}
            }
            refreshTags()
          }}
        />
      )}

      {/* Upload */}
      <MuxUpload
        onComplete={(assetId) => {
          setStatus(`✅ Upload complete — asset ${assetId}`)
          invalidateMuxCache()
          loadAssets(true)
        }}
        setStatus={setStatus}
      />

      {/* Filters bar */}
      <div className="mux-filters-bar">
        <div className="mux-filter-search">
          <input
            type="text"
            placeholder="Search by title..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="mux-filter-tag">
          <select value={activeTag} onChange={e => handleTagFilter(e.target.value)}>
            <option value="">All Tags</option>
            {(allTags || []).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {duplicateTitles.size > 0 && (
          <span className="mux-dupe-count">{duplicateTitles.size} duplicate title{duplicateTitles.size !== 1 ? 's' : ''}</span>
        )}

        {checked.size > 0 && (
          <div className="mux-filter-bulk">
            <span>{checked.size} selected</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowBulkTag(true)}>Tag</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowBulkCreateCatalog(true)}>Create Catalog</button>
            <button className="btn btn-ghost btn-sm mux-bulk-delete" onClick={handleBulkDelete}>Delete</button>
            <button className="btn btn-ghost btn-sm" onClick={clearSelection}>✕</button>
          </div>
        )}
      </div>

      {/* Asset table */}
      {loading && !assets.length ? (
        <div className="mux-empty">Loading Mux assets…</div>
      ) : !assets.length ? (
        <div className="mux-empty">
          <div className="icon">🎬</div>
          <p>No assets found in Mux. Restore videos from the Archive to get started.</p>
        </div>
      ) : (
        <div className="mux-vtable-wrap">
          <div className="mux-vtable-header" role="row">
            <div className="mux-vtable-cell" role="columnheader">
              <input
                type="checkbox"
                checked={displayAssets.length > 0 && checked.size === displayAssets.length}
                onChange={handleToggleAll}
              />
            </div>
            <div className="mux-vtable-cell" role="columnheader">Thumb</div>
            <div className="mux-vtable-cell mux-th-sortable" role="columnheader" onClick={() => handleSort('title')}>
              Title / ID {sortKey === 'title' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
            </div>
            <div className="mux-vtable-cell mux-th-sortable" role="columnheader" onClick={() => handleSort('status')}>
              Status {sortKey === 'status' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
            </div>
            <div className="mux-vtable-cell mux-th-sortable" role="columnheader" onClick={() => handleSort('duration')}>
              Duration {sortKey === 'duration' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
            </div>
            <div className="mux-vtable-cell mux-th-sortable" role="columnheader" onClick={() => handleSort('resolution')}>
              Resolution {sortKey === 'resolution' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
            </div>
            <div className="mux-vtable-cell" role="columnheader">Linked</div>
            <div className="mux-vtable-cell" role="columnheader">Tags</div>
            <div className="mux-vtable-cell mux-th-sortable" role="columnheader" onClick={() => handleSort('created')}>
              Created {sortKey === 'created' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
            </div>
          </div>
          <FixedSizeList
            height={tableHeight}
            itemCount={displayAssets.length}
            itemSize={MUX_ROW_HEIGHT}
            width="100%"
            overscanCount={6}
          >
            {({ index, style }) => {
              const asset = displayAssets[index]
              const pid = getPlaybackId(asset)
              const thumb = getMuxThumbnail(pid)
              const isSelected = selected?.id === asset.id
              const isChecked = checked.has(asset.id)
              const tbtvId = asset.meta?.external_id || muxLinks[asset.id]?.entries?.[0]?.tbtv_id || resolvedIds[asset.id]
              const tags = tbtvId && assetTagsMap[tbtvId]
              const link = muxLinks[asset.id]
              const titleLower = getAssetTitle(asset).toLowerCase()
              return (
                <div
                  key={asset.id}
                  style={style}
                  className={`mux-vtable-row ${isSelected ? 'selected' : ''}`}
                  onClick={() => { stopPlayer(); setSelected(asset) }}
                  role="row"
                >
                  <div className="mux-vtable-cell" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleToggle(asset.id)}
                    />
                  </div>
                  <div className="mux-vtable-cell">
                    {thumb ? (
                      <img src={thumb} alt="" className="mux-thumb" loading="lazy" decoding="async" />
                    ) : (
                      <div className="mux-thumb-empty" />
                    )}
                  </div>
                  <div className="mux-vtable-cell">
                    <div className={`mux-asset-title ${duplicateTitles.has(titleLower) ? 'mux-dupe' : ''}`}>
                      {getAssetTitle(asset)}
                      {duplicateTitles.has(titleLower) && (
                        <span className="mux-dupe-badge">DUPE</span>
                      )}
                    </div>
                    <div className="mux-asset-id">{asset.id}</div>
                  </div>
                  <div className="mux-vtable-cell">
                    <span className={`mux-status-badge ${asset.status}`}>{asset.status}</span>
                  </div>
                  <div className="mux-vtable-cell">{formatDuration(asset.duration)}</div>
                  <div className="mux-vtable-cell">{asset.resolution_tier || asset.max_resolution_tier || '—'}</div>
                  <div className="mux-vtable-cell mux-td-linked">
                    {!link?.linked ? (
                      <span className="mux-link-none">—</span>
                    ) : (
                      <span className="mux-link-badge" title={link.entries.map(e => e.title).join(', ')}>
                        🔗 {link.entries.length}
                      </span>
                    )}
                  </div>
                  <div className="mux-vtable-cell mux-td-tags">
                    {!tags || !tags.length ? '—' : (
                      <div className="mux-table-tags">
                        {tags.slice(0, 2).map(t => (
                          <span key={t} className="mux-tag-chip-mini">{t}</span>
                        ))}
                        {tags.length > 2 && (
                          <span className="mux-tag-chip-mini more">+{tags.length - 2}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="mux-vtable-cell">{formatDate(asset.created_at)}</div>
                </div>
              )
            }}
          </FixedSizeList>
        </div>
      )}

      {/* Migrate from CF Stream modal */}
      {showMigrateModal && (
        <MigrateFromCFModal
          onClose={() => setShowMigrateModal(false)}
          onComplete={() => { invalidateMuxCache(); loadAssets(true) }}
        />
      )}

      {/* Restore from Archive modal */}
      {showRestoreModal && (
        <RestoreModal
          files={archiveFiles}
          loading={archiveLoading}
          busy={restoreBusy}
          status={restoreStatus}
          results={restoreResults}
          onRestore={handleRestoreToMux}
          onClose={() => { setShowRestoreModal(false); setRestoreStatus(''); setRestoreResults([]) }}
        />
      )}

      {/* Bulk Tag modal */}
      {showBulkTag && (
        <BulkTagModal
          items={getCheckedItems()}
          idKey="archive_key"
          onClose={() => setShowBulkTag(false)}
          onComplete={async () => {
            setShowBulkTag(false)
            setStatus('Tags applied — refreshing...')
            const checkedAssets = assets.filter(a => checked.has(a.id))
            const newResolved = {}
            const newTagMap = {}
            for (const asset of checkedAssets) {
              // Use content library tbtv_id if linked, otherwise resolve
              let tbtvId = asset.meta?.external_id || muxLinks[asset.id]?.entries?.[0]?.tbtv_id || resolvedIds[asset.id]
              if (!tbtvId && asset.passthrough) {
                try {
                  const data = await api('/content/lookup-keys', {
                    method: 'POST',
                    body: JSON.stringify({ archive_keys: [asset.passthrough] })
                  })
                  tbtvId = data.results?.[asset.passthrough]
                } catch {}
              }
              if (tbtvId) {
                newResolved[asset.id] = tbtvId
                try {
                  const tagData = await api(`/tags/${tbtvId}`)
                  newTagMap[tbtvId] = tagData.tags || []
                } catch {}
              }
            }
            setResolvedIds(prev => ({ ...prev, ...newResolved }))
            setAssetTagsMap(prev => ({ ...prev, ...newTagMap }))
            clearSelection()
            refreshTags()
            setStatus('✅ Tags applied')
          }}
        />
      )}

      {/* Bulk Create Catalog Items from Mux assets */}
      {showBulkCreateCatalog && (
        <BulkCreateCatalogModal
          selectedAssets={assets.filter(a => checked.has(a.id))}
          muxLinks={muxLinks}
          onClose={() => setShowBulkCreateCatalog(false)}
          onComplete={(ok, fail) => {
            setStatus(`✅ Created ${ok} catalog item${ok !== 1 ? 's' : ''}${fail ? ` (${fail} failed)` : ''}`)
            clearSelection()
            invalidateMuxCache()
            loadAssets(true)
          }}
        />
      )}
    </div>
  )
}

// ============================================================
// ASSET DETAIL PANEL
// ============================================================

function AssetDetail({ asset, onClose, onDelete, onPlay, playingAssetId, playerWrapperRef, stopPlayer, api, allTags, muxLinks, onAssetUpdated, onTagsSaved }) {
  const pid = getPlaybackId(asset)
  const thumb = getMuxThumbnail(pid)
  const hlsUrl = getMuxHls(pid)
  const isPlaying = playingAssetId === asset.id
  const meta = getAssetMeta(asset)

  // Title editing state
  const [editTitle, setEditTitle] = useState(meta.title || '')
  const [titleSaving, setTitleSaving] = useState(false)
  const [enablingMp4, setEnablingMp4] = useState(false)
  const [mp4Status, setMp4Status] = useState('')

  // Content library link info
  const link = muxLinks?.[asset.id]
  const linkedTitle = link?.entries?.[0]?.title

  // Reset title when asset changes
  useEffect(() => { setEditTitle(meta.title || '') }, [asset.id, meta.title])

  async function handleSaveTitle() {
    if (!editTitle.trim() || editTitle === meta.title) return
    setTitleSaving(true)
    try {
      await api(`/mux/assets/${asset.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ title: editTitle.trim() })
      })
      if (onAssetUpdated) onAssetUpdated()
    } catch (e) {
      setTagStatus(`Title save failed: ${e.message}`)
    }
    setTitleSaving(false)
  }

  async function handleSyncTitleFromLibrary() {
    if (!linkedTitle) return
    setEditTitle(linkedTitle)
    setTitleSaving(true)
    try {
      await api(`/mux/assets/${asset.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ title: linkedTitle })
      })
      if (onAssetUpdated) onAssetUpdated()
    } catch (e) {
      setTagStatus(`Sync failed: ${e.message}`)
    }
    setTitleSaving(false)
  }

  // Trigger Mux MP4 generation for an asset that came in without static
  // renditions (e.g., livestream recordings). Defaults to capped-1080p so file
  // sizes stay reasonable on 4K sources; users can re-enable as 'standard' via
  // the Mux dashboard or curl if they want full-source quality.
  async function handleEnableMp4() {
    setEnablingMp4(true)
    setMp4Status('')
    try {
      await api(`/mux/assets/${asset.id}/mp4-support`, {
        method: 'POST',
        body: JSON.stringify({ mp4_support: 'capped-1080p' }),
      })
      setMp4Status('✓ MP4 generation queued — usually ready in 1–3 minutes. Close and reopen this asset to check.')
      if (onAssetUpdated) onAssetUpdated()
    } catch (e) {
      setMp4Status(`Error: ${e.message}`)
    }
    setEnablingMp4(false)
  }

  // Tag editing state
  const [tags, setTags] = useState([])
  const [tagInput, setTagInput] = useState('')
  const [tagSuggestions, setTagSuggestions] = useState([])
  const [tagLoading, setTagLoading] = useState(false)
  const [tagStatus, setTagStatus] = useState('')
  const [tbtvId, setTbtvId] = useState(meta.external_id || null)
  const tagInputRef = useRef()

  // Load tags for this asset
  useEffect(() => {
    let cancelled = false
    async function loadTags() {
      setTagLoading(true)
      setTagStatus('')
      try {
        let resolvedId = meta.external_id
        // If no tbtv_id, try resolving via archive_key (passthrough)
        if (!resolvedId) {
          // For Mux assets, resolve via archive_key (passthrough) when present,
          // otherwise via mux_asset_id (covers livestream recordings and any
          // other Mux asset without an archive-key backing).
          const resolveBody = meta.source_file
            ? { archive_key: meta.source_file, title: meta.title || meta.source_file, content_type: 'stream' }
            : { mux_asset_id: asset.id, title: meta.title || getAssetTitle(asset), content_type: 'stream' }
          const res = await api('/content/resolve', {
            method: 'POST',
            body: JSON.stringify(resolveBody)
          })
          resolvedId = res.tbtv_id
        }
        if (!cancelled && resolvedId) {
          setTbtvId(resolvedId)
          const tagData = await api(`/tags/${resolvedId}`)
          if (!cancelled) setTags(tagData.tags || [])
        }
      } catch {
        // No content record yet — that's fine, tags start empty
      }
      if (!cancelled) setTagLoading(false)
    }
    loadTags()
    return () => { cancelled = true }
  }, [asset.id, api, meta.external_id, meta.source_file, meta.title])

  // Tag autocomplete suggestions
  useEffect(() => {
    if (tagInput.trim()) {
      const matches = (allTags || []).filter(t =>
        t.includes(tagInput.toLowerCase()) && !tags.includes(t)
      )
      setTagSuggestions(matches.slice(0, 8))
    } else {
      setTagSuggestions([])
    }
  }, [tagInput, allTags, tags])

  function addTag(tag) {
    const t = tag.trim().toLowerCase()
    if (t && !tags.includes(t)) {
      setTags(prev => [...prev, t])
    }
    setTagInput('')
    setTagSuggestions([])
    tagInputRef.current?.focus()
  }

  function removeTag(tag) {
    setTags(prev => prev.filter(t => t !== tag))
  }

  function handleTagKeyDown(e) {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault()
      addTag(tagInput)
    }
    if (e.key === 'Backspace' && !tagInput && tags.length) {
      setTags(prev => prev.slice(0, -1))
    }
  }

  async function handleSaveTags() {
    setTagStatus('')
    try {
      let resolvedId = tbtvId
      if (!resolvedId) {
        // Create/find content record. Prefer archive_key (passthrough) for
        // Mux assets that came from the R2 archive; fall back to mux_asset_id
        // for Mux-only assets (livestream recordings, direct uploads, clips).
        const resolveBody = meta.source_file
          ? { archive_key: meta.source_file, title: meta.title || getAssetTitle(asset), content_type: 'stream' }
          : { mux_asset_id: asset.id, title: meta.title || getAssetTitle(asset), content_type: 'stream' }
        const res = await api('/content/resolve', {
          method: 'POST',
          body: JSON.stringify(resolveBody)
        })
        resolvedId = res.tbtv_id
        setTbtvId(resolvedId)
      }
      await api(`/tags/${resolvedId}`, {
        method: 'PUT',
        body: JSON.stringify({ tags })
      })
      setTagStatus('Tags saved')
      if (onTagsSaved) onTagsSaved(asset.id, resolvedId)
    } catch (e) {
      setTagStatus(`Error: ${e.message}`)
    }
  }

  return (
    <div className="mux-detail card">
      <div className="mux-detail-header">
        <h3>{meta.title || asset.id}</h3>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕ Close</button>
      </div>

      <div className="mux-detail-grid">
        {/* Left: thumbnail / player */}
        <div className="mux-detail-thumb">
          {isPlaying ? (
            <div className="mux-player-wrap">
              <div ref={playerWrapperRef} />
              <button className="mux-player-stop" onClick={stopPlayer} title="Stop">✕</button>
            </div>
          ) : thumb ? (
            <div className="mux-thumb-preview" onClick={() => onPlay(asset)}>
              <img src={thumb} alt="" />
              {hlsUrl && <div className="mux-play-overlay"><span className="mux-play-icon">▶</span></div>}
            </div>
          ) : (
            <div className="mux-detail-nothumb">No playback available</div>
          )}
        </div>

        {/* Right: metadata */}
        <div className="mux-detail-meta">
          <div className="mux-meta-row mux-title-edit-row">
            <span className="mux-meta-label">Title</span>
            <div className="mux-title-edit">
              <input
                type="text"
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveTitle() }}
                className="mux-title-input"
              />
              {editTitle !== (meta.title || '') && (
                <button className="btn btn-ghost btn-sm" onClick={handleSaveTitle} disabled={titleSaving}>
                  {titleSaving ? '...' : 'Save'}
                </button>
              )}
              {linkedTitle && linkedTitle !== editTitle && (
                <button
                  className="btn btn-ghost btn-sm mux-sync-title-btn"
                  onClick={handleSyncTitleFromLibrary}
                  disabled={titleSaving}
                  title={`Sync from content library: "${linkedTitle}"`}
                >
                  🔗 Sync
                </button>
              )}
            </div>
          </div>
          {meta.creator_id && (
            <div className="mux-meta-row">
              <span className="mux-meta-label">Creator</span>
              <span className="mux-meta-value">{meta.creator_id}</span>
            </div>
          )}
          {meta.external_id && (
            <div className="mux-meta-row">
              <span className="mux-meta-label">TBTV ID</span>
              <span className="mux-meta-value mono">{meta.external_id}</span>
            </div>
          )}
          {meta.source_file && (
            <div className="mux-meta-row">
              <span className="mux-meta-label">Source File</span>
              <span className="mux-meta-value mono" style={{ fontSize: 11 }}>{meta.source_file}</span>
            </div>
          )}
          <div className="mux-meta-row">
            <span className="mux-meta-label">Asset ID</span>
            <span className="mux-meta-value mono">{asset.id}</span>
          </div>
          <div className="mux-meta-row">
            <span className="mux-meta-label">Status</span>
            <span className="mux-meta-value">
              <span className={`mux-status-badge ${asset.status}`}>{asset.status}</span>
            </span>
          </div>
          <div className="mux-meta-row">
            <span className="mux-meta-label">Duration</span>
            <span className="mux-meta-value">{formatDuration(asset.duration)}</span>
          </div>
          <div className="mux-meta-row">
            <span className="mux-meta-label">Resolution</span>
            <span className="mux-meta-value">{asset.resolution_tier || asset.max_resolution_tier || '—'}</span>
          </div>
          <div className="mux-meta-row">
            <span className="mux-meta-label">Aspect Ratio</span>
            <span className="mux-meta-value">{asset.aspect_ratio || '—'}</span>
          </div>
          <div className="mux-meta-row">
            <span className="mux-meta-label">Created</span>
            <span className="mux-meta-value">{formatDate(asset.created_at)}</span>
          </div>
          {pid && (
            <div className="mux-meta-row">
              <span className="mux-meta-label">Playback ID</span>
              <span className="mux-meta-value mono">{pid}</span>
            </div>
          )}
          {hlsUrl && (
            <div className="mux-meta-row">
              <span className="mux-meta-label">HLS URL</span>
              <span className="mux-meta-value mono" style={{ fontSize: 10, wordBreak: 'break-all' }}>{hlsUrl}</span>
            </div>
          )}
          {asset.tracks && asset.tracks.length > 0 && (
            <div className="mux-meta-row">
              <span className="mux-meta-label">Tracks</span>
              <span className="mux-meta-value">
                {asset.tracks.map((t, i) => (
                  <span key={i} className="mux-track-badge">{t.type}{t.max_width ? ` ${t.max_width}×${t.max_height}` : ''}</span>
                ))}
              </span>
            </div>
          )}

          {/* Tags */}
          <div className="mux-meta-row mux-tags-row">
            <span className="mux-meta-label">Tags</span>
            <div className="mux-meta-value">
              {tagLoading ? (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Loading…</span>
              ) : (
                <div className="mux-tag-editor">
                  <div className="mux-tag-chips">
                    {tags.map(t => (
                      <span key={t} className="mux-tag-chip">
                        {t}
                        <button onClick={() => removeTag(t)}>✕</button>
                      </span>
                    ))}
                    <input
                      ref={tagInputRef}
                      type="text"
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      placeholder={tags.length ? '' : 'Add tags…'}
                      className="mux-tag-input"
                    />
                  </div>
                  {tagSuggestions.length > 0 && (
                    <div className="mux-tag-suggestions">
                      {tagSuggestions.map(s => (
                        <button key={s} onClick={() => addTag(s)}>{s}</button>
                      ))}
                    </div>
                  )}
                  <div className="mux-tag-actions">
                    <button className="btn btn-ghost btn-sm" onClick={handleSaveTags}>Save Tags</button>
                    {tagStatus && <span className="mux-tag-status">{tagStatus}</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mux-detail-actions">
        {/* MP4 Downloads */}
        {(() => {
          if (!pid) {
            return (
              <span className="mux-download-label" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                MP4 downloads require a playback ID
              </span>
            )
          }
          const renditions = asset.static_renditions
          const files = (renditions?.files || []).filter(f => f.name)
          // Renditions ready AND we have actual file names → link to whatever Mux generated
          if (renditions?.status === 'ready' && files.length > 0) {
            // Sort by height desc so "Highest" appears first.
            const sorted = [...files].sort((a, b) => (b.height || 0) - (a.height || 0))
            return (
              <div className="mux-download-row">
                <span className="mux-download-label">Download MP4:</span>
                {sorted.map(f => {
                  const label = f.height ? `${f.height}p` : f.name.replace(/\.mp4$/i, '')
                  return (
                    <a
                      key={f.name}
                      href={`https://stream.mux.com/${pid}/${f.name}`}
                      download
                      className="btn btn-ghost btn-sm"
                      title={`${f.name} · ${f.width || '?'}×${f.height || '?'}${f.filesize ? ` · ${(f.filesize / 1048576).toFixed(1)} MB` : ''}`}
                    >
                      {label}
                    </a>
                  )
                })}
              </div>
            )
          }
          // Renditions ready but no files reported — odd state, fall through to legacy URLs only
          // if explicitly mp4_support === 'standard' (legacy convention).
          if (renditions?.status === 'ready' && files.length === 0 && asset.mp4_support === 'standard') {
            return (
              <div className="mux-download-row">
                <span className="mux-download-label">Download MP4:</span>
                <a href={`https://stream.mux.com/${pid}/high.mp4`} download className="btn btn-ghost btn-sm">High</a>
                <a href={`https://stream.mux.com/${pid}/medium.mp4`} download className="btn btn-ghost btn-sm">Medium</a>
                <a href={`https://stream.mux.com/${pid}/low.mp4`} download className="btn btn-ghost btn-sm">Low</a>
              </div>
            )
          }
          // Renditions failed
          if (renditions?.status === 'errored') {
            return (
              <div className="mux-download-row">
                <span className="mux-download-label" style={{ fontSize: 11, color: '#ef4444' }}>
                  ✕ MP4 generation failed.
                </span>
                <button className="btn btn-ghost btn-sm" onClick={handleEnableMp4} disabled={enablingMp4}>
                  {enablingMp4 ? 'Retrying…' : 'Retry'}
                </button>
              </div>
            )
          }
          // Enabled but preparing (status === 'preparing' OR mp4_support set with no status yet)
          if (renditions?.status === 'preparing'
              || (asset.mp4_support && asset.mp4_support !== 'none')) {
            return (
              <div className="mux-download-row">
                <span className="mux-download-label" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  ⏳ MP4 renditions preparing ({asset.mp4_support || 'enabled'}) — close and reopen this asset in 1–3 minutes to check.
                </span>
              </div>
            )
          }
          // Not enabled — offer to enable
          return (
            <div className="mux-download-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="mux-download-label">MP4 downloads:</span>
                <button className="btn btn-ghost btn-sm" onClick={handleEnableMp4} disabled={enablingMp4}>
                  {enablingMp4 ? 'Enabling…' : '↓ Enable MP4 Downloads'}
                </button>
              </div>
              {mp4Status && (
                <span style={{ fontSize: 11, color: mp4Status.startsWith('Error') ? '#ef4444' : '#22c55e' }}>
                  {mp4Status}
                </span>
              )}
            </div>
          )
        })()}
        <button className="btn btn-danger btn-sm" onClick={() => onDelete(asset.id)}>Delete from Mux</button>
      </div>
    </div>
  )
}

// ============================================================
// RESTORE FROM ARCHIVE MODAL
// ============================================================

function RestoreModal({ files, loading, busy, status, results, onRestore, onClose }) {
  const [search, setSearch] = useState('')
  const [checked, setChecked] = useState(new Set())

  const filtered = files.filter(f =>
    (f.filename || f).toLowerCase().includes(search.toLowerCase())
  )

  const toggleFile = (fname) => {
    setChecked(prev => {
      const next = new Set(prev)
      if (next.has(fname)) next.delete(fname)
      else next.add(fname)
      return next
    })
  }

  const toggleAll = () => {
    if (checked.size === filtered.length) {
      setChecked(new Set())
    } else {
      setChecked(new Set(filtered.map(f => f.filename || f)))
    }
  }

  const handleRestore = () => {
    const items = [...checked].map(fname => ({
      filename: fname,
      title: fname.replace(/\.mp4$/i, ''),
    }))
    onRestore(items)
  }

  return (
    <div className="mux-modal-backdrop" onClick={onClose}>
      <div className="mux-modal" onClick={e => e.stopPropagation()}>
        <div className="mux-modal-header">
          <h3>Restore to Mux from Archive</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        <div className="mux-modal-body">
          {/* Search + select all */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
            <input
              type="text"
              placeholder="Search archive files…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="mux-search-input"
              style={{ flex: 1, marginBottom: 0 }}
            />
            {filtered.length > 0 && (
              <button className="btn btn-ghost btn-sm" onClick={toggleAll} style={{ whiteSpace: 'nowrap', fontSize: 11 }}>
                {checked.size === filtered.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>

          {/* File list with checkboxes */}
          <div className="mux-restore-list">
            {loading ? (
              <div className="mux-restore-empty">Loading archive…</div>
            ) : filtered.length === 0 ? (
              <div className="mux-restore-empty">No files found</div>
            ) : (
              filtered.map((file, idx) => {
                const fname = file.filename || file
                const isChecked = checked.has(fname)
                return (
                  <div
                    key={idx}
                    className={`mux-restore-item ${isChecked ? 'selected' : ''}`}
                    onClick={() => toggleFile(fname)}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {}}
                      style={{ marginRight: 8, flexShrink: 0 }}
                    />
                    <span className="mux-restore-name">{fname}</span>
                    {file.size && <span className="mux-restore-size">{formatBytes(file.size)}</span>}
                  </div>
                )
              })
            )}
          </div>

          {/* Action bar */}
          {checked.size > 0 && (
            <div className="mux-restore-action">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {checked.size} file{checked.size > 1 ? 's' : ''} selected — titles default to filename
                </span>
                <button
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={handleRestore}
                >
                  {busy ? 'Ingesting…' : `Restore ${checked.size} to Mux`}
                </button>
              </div>
              {status && <div className="mux-restore-status">{status}</div>}
              {results && results.length > 0 && (
                <div className="mux-restore-results">
                  {results.map((r, i) => (
                    <div key={i} className={`mux-restore-result ${r.ok ? 'ok' : 'fail'}`}>
                      <span className="mux-restore-result-icon">{r.ok ? '✓' : '✕'}</span>
                      <span className="mux-restore-name">{r.filename}</span>
                      {r.ok ? (
                        <span className="mux-restore-result-info">
                          <span className="mux-restore-size">{r.asset_id}</span>
                          {r.copiedTags && r.copiedTags.length > 0 && (
                            <span className="mux-restore-tags">Tags: {r.copiedTags.join(', ')}</span>
                          )}
                        </span>
                      ) : (
                        <span className="mux-restore-error">{r.error}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
